import React, { useState, useEffect, useCallback } from 'react';
import { faqAdminApi, FAQ, CreateFAQRequest, UpdateFAQRequest } from '../services/faqApi';
import { FiHelpCircle, FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiRefreshCw } from 'react-icons/fi';
import './FAQManagement.css';

const FAQManagement: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState<CreateFAQRequest>({
    questionAr: '',
    questionEn: '',
    answerAr: '',
    answerEn: '',
    displayOrder: 0,
    isActive: true,
  });

  const loadFAQs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await faqAdminApi.getAll();
      const sortedFaqs = (response.faqs || []).sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return a.displayOrder - b.displayOrder;
        }
        return (b.createdAt || '').localeCompare(a.createdAt || '');
      });
      setFaqs(sortedFaqs);
    } catch (error: any) {
      console.error('Failed to load FAQs:', error);
      alert(`Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFAQs();
  }, [loadFAQs]);

  const handleCreate = async () => {
    if (!formData.questionAr || !formData.questionEn || !formData.answerAr || !formData.answerEn) {
      alert('Please fill in all required fields (question and answer in both Arabic and English)');
      return;
    }

    try {
      await faqAdminApi.create(formData);
      await loadFAQs();
      handleCancel();
      alert('FAQ created successfully!');
    } catch (error: any) {
      console.error('Failed to create FAQ:', error);
      alert(`Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleEdit = (faq: FAQ) => {
    setEditingId(faq.id);
    setFormData({
      questionAr: faq.questionAr,
      questionEn: faq.questionEn,
      answerAr: faq.answerAr,
      answerEn: faq.answerEn,
      displayOrder: faq.displayOrder,
      isActive: faq.isActive,
    });
    setShowCreateForm(false);
  };

  const handleUpdate = async (id: number) => {
    try {
      const updateData: UpdateFAQRequest = {
        questionAr: formData.questionAr,
        questionEn: formData.questionEn,
        answerAr: formData.answerAr,
        answerEn: formData.answerEn,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
      };
      await faqAdminApi.update(id, updateData);
      await loadFAQs();
      handleCancel();
      alert('FAQ updated successfully!');
    } catch (error: any) {
      console.error('Failed to update FAQ:', error);
      alert(`Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDelete = async (id: number, question: string) => {
    if (!window.confirm(`Are you sure you want to delete this FAQ?\n\n"${question}"\n\nThis will set it to inactive.`)) {
      return;
    }

    try {
      await faqAdminApi.delete(id);
      await loadFAQs();
      alert('FAQ deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete FAQ:', error);
      alert(`Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowCreateForm(false);
    setFormData({
      questionAr: '',
      questionEn: '',
      answerAr: '',
      answerEn: '',
      displayOrder: 0,
      isActive: true,
    });
  };

  return (
    <div className="faq-management">
      <div className="faq-header">
        <h2>
          {React.createElement(FiHelpCircle as any, { className: "header-icon" })}
          FAQ Management
        </h2>
        <div className="header-actions">
          <button className="btn-refresh" onClick={loadFAQs} disabled={loading}>
            {React.createElement(FiRefreshCw as any, { className: loading ? 'spinning' : '' })}
            Refresh
          </button>
          <button className="btn-add" onClick={() => {
            handleCancel();
            setShowCreateForm(true);
          }}>
            {React.createElement(FiPlus as any)} Add FAQ
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="faq-form-card">
          <h3>Create New FAQ</h3>
          <div className="faq-form">
            <div className="form-row">
              <div className="form-group">
                <label>Question (English) *</label>
                <input
                  type="text"
                  value={formData.questionEn}
                  onChange={(e) => setFormData({ ...formData, questionEn: e.target.value })}
                  placeholder="e.g., How can I create a new account?"
                />
              </div>
              <div className="form-group">
                <label>Question (Arabic) *</label>
                <input
                  type="text"
                  value={formData.questionAr}
                  onChange={(e) => setFormData({ ...formData, questionAr: e.target.value })}
                  placeholder="e.g., كيف يمكنني إنشاء حساب جديد؟"
                  dir="rtl"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Answer (English) *</label>
                <textarea
                  value={formData.answerEn}
                  onChange={(e) => setFormData({ ...formData, answerEn: e.target.value })}
                  placeholder="Enter the answer in English..."
                  rows={5}
                />
              </div>
              <div className="form-group">
                <label>Answer (Arabic) *</label>
                <textarea
                  value={formData.answerAr}
                  onChange={(e) => setFormData({ ...formData, answerAr: e.target.value })}
                  placeholder="أدخل الإجابة بالعربية..."
                  dir="rtl"
                  rows={5}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={formData.displayOrder || 0}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive !== false}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn-save" onClick={handleCreate}>
                {React.createElement(FiSave as any)} Create FAQ
              </button>
              <button className="btn-cancel" onClick={handleCancel}>
                {React.createElement(FiX as any)} Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading FAQs...</p>
        </div>
      ) : faqs.length === 0 ? (
        <div className="empty-state">
          <p>No FAQs found. Click "Add FAQ" to create your first FAQ.</p>
        </div>
      ) : (
        <div className="faqs-list">
          {faqs.map((faq) => {
            const isEditing = editingId === faq.id;
            return (
              <div key={faq.id} className={`faq-card ${!faq.isActive ? 'inactive' : ''}`}>
                {isEditing ? (
                  <div className="faq-edit-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Question (English) *</label>
                        <input
                          type="text"
                          value={formData.questionEn}
                          onChange={(e) => setFormData({ ...formData, questionEn: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Question (Arabic) *</label>
                        <input
                          type="text"
                          value={formData.questionAr}
                          onChange={(e) => setFormData({ ...formData, questionAr: e.target.value })}
                          dir="rtl"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Answer (English) *</label>
                        <textarea
                          value={formData.answerEn}
                          onChange={(e) => setFormData({ ...formData, answerEn: e.target.value })}
                          rows={5}
                        />
                      </div>
                      <div className="form-group">
                        <label>Answer (Arabic) *</label>
                        <textarea
                          value={formData.answerAr}
                          onChange={(e) => setFormData({ ...formData, answerAr: e.target.value })}
                          dir="rtl"
                          rows={5}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Display Order</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.displayOrder || 0}
                          onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={formData.isActive !== false}
                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          />
                          Active
                        </label>
                      </div>
                    </div>
                    <div className="form-actions">
                      <button className="btn-save" onClick={() => handleUpdate(faq.id)}>
                        {React.createElement(FiSave as any)} Save
                      </button>
                      <button className="btn-cancel" onClick={handleCancel}>
                        {React.createElement(FiX as any)} Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="faq-header-row">
                      <div className="faq-content">
                        <div className="faq-question">
                          <h3>{faq.questionEn}</h3>
                          <p dir="rtl" className="faq-question-ar">{faq.questionAr}</p>
                        </div>
                        <div className="faq-answer">
                          <p>{faq.answerEn}</p>
                          <p dir="rtl" className="faq-answer-ar">{faq.answerAr}</p>
                        </div>
                      </div>
                      <div className="faq-meta">
                        <span className="faq-order">Order: {faq.displayOrder}</span>
                        <span className={`faq-status ${faq.isActive ? 'active' : 'inactive'}`}>
                          {faq.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="faq-actions">
                      <button className="btn-edit" onClick={() => handleEdit(faq)}>
                        {React.createElement(FiEdit2 as any)} Edit
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(faq.id, faq.questionEn)}>
                        {React.createElement(FiTrash2 as any)} Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FAQManagement;

